// Flatten 3D spherical panels into a printable SVG sheet with seam allowance
// and stitch holes that align across shared edges.
//
// Coordinate conventions:
// - Input vertices are unit-length on the ball.
// - Output units are millimetres (SVG uses width="XXmm").
// - Stitch holes are placed in 3D on every unique edge with a shared parametric
//   spacing, then projected into each adjacent panel's local 2D plane. This
//   guarantees the hole count and t-values match between neighbours, so panels
//   line up perfectly when stitched.

import { smoothPerimeter } from './smooth';
import polygonClipping from 'polygon-clipping';

const sub3 = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const add3 = (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const scale3 = (a, s) => [a[0] * s, a[1] * s, a[2] * s];
const cross3 = (a, b) => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const dot3 = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const len3 = (v) => Math.hypot(v[0], v[1], v[2]);
const n3 = (v) => {
  const l = len3(v) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
};

// Compute the plane of a face (Newell's method → outward-pointing normal for
// CCW-when-viewed-from-outside polygons) and a 2D basis (u, v) inside it.
// If the raw normal points TOWARD the ball origin — which happens whenever
// a panel's 3D winding is CW-when-viewed-from-outside (e.g. mirror-symmetry
// siblings, or a freehand stroke drawn the "wrong way") — we flip it so the
// flat pattern is always viewed from the OUTSIDE of the ball. Without this
// flip the exported sheet came out mirror-imaged for half the panels.
function faceFrame(face3D) {
  const n = face3D.length;
  const centroid = [0, 0, 0];
  for (const p of face3D) {
    centroid[0] += p[0];
    centroid[1] += p[1];
    centroid[2] += p[2];
  }
  centroid[0] /= n;
  centroid[1] /= n;
  centroid[2] /= n;

  const normal = [0, 0, 0];
  for (let i = 0; i < n; i++) {
    const a = face3D[i];
    const b = face3D[(i + 1) % n];
    normal[0] += (a[1] - b[1]) * (a[2] + b[2]);
    normal[1] += (a[2] - b[2]) * (a[0] + b[0]);
    normal[2] += (a[0] - b[0]) * (a[1] + b[1]);
  }
  let nrm = n3(normal);
  // The panel sits on a sphere centred at origin, so the outward direction
  // for its centroid is just the centroid itself. If they point opposite
  // ways the Newell normal is inward → flip it.
  if (nrm[0] * centroid[0] + nrm[1] * centroid[1] + nrm[2] * centroid[2] < 0) {
    nrm = [-nrm[0], -nrm[1], -nrm[2]];
  }
  const up = Math.abs(nrm[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
  const u = n3(cross3(up, nrm));
  const v = n3(cross3(nrm, u));
  return { centroid, normal: nrm, u, v };
}

function project(point3D, frame) {
  const d = sub3(point3D, frame.centroid);
  // Negate the v component so SVG Y-down renders "up on the ball" at the
  // top of the sheet. Without this flip the exported panel appears
  // vertically inverted relative to how it sits on the 3D model.
  return [dot3(d, frame.u), -dot3(d, frame.v)];
}

// Compute stitch-hole 3D positions along every unique edge. Holes are
// symmetric around the edge midpoint at fixed spacing so adjacent panels get
// identical hole positions. Each hole receives a global integer ID so labels
// on adjacent panels match: hole #42 on Panel A stitches to hole #42 on Panel B.
function computeEdgeHoles(vertices3D, faces, holeSpacingMM, spherized, verticesUnit) {
  const edges = new Map();
  const key = (a, b) => (a < b ? `${a}_${b}` : `${b}_${a}`);
  let nextId = 1;
  const orderedKeys = [];
  for (const f of faces) {
    for (let i = 0; i < f.length; i++) {
      const a = f[i];
      const b = f[(i + 1) % f.length];
      const k = key(a, b);
      if (edges.has(k)) continue;
      const va = vertices3D[a];
      const vb = vertices3D[b];

      let L, N, tStart, step;
      if (spherized && verticesUnit) {
        // Space holes by arc length along the great circle between endpoints
        const ua = verticesUnit[a];
        const ub = verticesUnit[b];
        const dotAB = Math.max(-1, Math.min(1, ua[0] * ub[0] + ua[1] * ub[1] + ua[2] * ub[2]));
        const angle = Math.acos(dotAB);
        L = len3(sub3(vb, va)) / (2 * Math.sin(angle / 2)) * angle; // R * angle
        // Simpler: L = R * angle where R = |va| = |vb|
        const R = Math.hypot(va[0], va[1], va[2]);
        L = R * angle;
      } else {
        L = len3(sub3(vb, va));
      }

      N = Math.max(2, Math.floor(L / holeSpacingMM));
      tStart = (L - (N - 1) * holeSpacingMM) / (2 * L);
      step = holeSpacingMM / L;
      const holes = [];
      for (let m = 0; m < N; m++) {
        const t = tStart + m * step;
        let point3D;
        if (spherized && verticesUnit) {
          // slerp on unit sphere, then scale to real radius
          const R = Math.hypot(va[0], va[1], va[2]);
          const uni = slerpUnit(verticesUnit[a], verticesUnit[b], t);
          point3D = [uni[0] * R, uni[1] * R, uni[2] * R];
        } else {
          point3D = add3(scale3(va, 1 - t), scale3(vb, t));
        }
        holes.push({ point3D, id: nextId++ });
      }
      edges.set(k, holes);
      orderedKeys.push(k);
    }
  }
  return edges;
}

// Great-circle interpolation between two unit-sphere directions.
function slerpUnit(a, b, t) {
  const d = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
  const theta = Math.acos(d);
  const s = Math.sin(theta);
  if (Math.abs(s) < 1e-9) return [a[0], a[1], a[2]];
  const w1 = Math.sin((1 - t) * theta) / s;
  const w2 = Math.sin(t * theta) / s;
  return [a[0] * w1 + b[0] * w2, a[1] * w1 + b[1] * w2, a[2] * w1 + b[2] * w2];
}

// Azimuthal-equidistant projection from a unit-sphere pole `cHat`, radius R.
// Preserves arc distance from cHat and preserves azimuth angle around cHat,
// so cutting the flat panel and curling it up produces a spherical cap of
// the correct area. Radial edges (centroid→vertex) become straight lines,
// tangential edges (vertex→vertex) become slight arcs — we sample them.
function makeAzimuthalProjector(cHat, R) {
  const up = Math.abs(cHat[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
  const d = dot3(cHat, up);
  const u = n3([up[0] - d * cHat[0], up[1] - d * cHat[1], up[2] - d * cHat[2]]);
  const w = cross3(cHat, u);
  return (v) => {
    const c = Math.max(-1, Math.min(1, dot3(v, cHat)));
    const phi = Math.acos(c);
    const arc = R * phi;
    const perpX = v[0] - c * cHat[0];
    const perpY = v[1] - c * cHat[1];
    const perpZ = v[2] - c * cHat[2];
    const perpLen = Math.hypot(perpX, perpY, perpZ);
    if (perpLen < 1e-9) return [0, 0];
    const uc = (perpX * u[0] + perpY * u[1] + perpZ * u[2]) / perpLen;
    const vc = (perpX * w[0] + perpY * w[1] + perpZ * w[2]) / perpLen;
    // SVG uses Y-down; negating the v component flips the output so "up on
    // the ball" lands at the top of the sheet. Signed-area based offset
    // routines downstream auto-detect winding, so the flip is safe.
    return [arc * uc, -arc * vc];
  };
}

// Signed area of a 2D polygon (positive for CCW winding).
function signedArea(poly) {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    a += p[0] * q[1] - q[0] * p[1];
  }
  return a * 0.5;
}

// Drop consecutive vertices that are nearly identical or lie almost exactly
// on a straight line through their neighbours. Uses a very small tolerance
// so we only remove true duplicates / collinear samples — real curve
// samples on a smooth arc must be preserved or the offset outline will
// look angular. `distTol` is the merge threshold for near-duplicate points
// (in mm). `perpTol` is the perpendicular threshold for collinear points;
// defaults to distTol but callers can pass a smaller value to preserve
// curves aggressively.
function simplifyPolygon(poly, distTol, perpTol) {
  const n = poly.length;
  if (n < 4) return poly.slice();
  const pTol = perpTol == null ? distTol : perpTol;
  const out = [];
  for (let i = 0; i < n; i++) {
    const prev = out.length > 0 ? out[out.length - 1] : poly[(i - 1 + n) % n];
    const curr = poly[i];
    const next = poly[(i + 1) % n];
    if (Math.hypot(curr[0] - prev[0], curr[1] - prev[1]) < distTol) continue;
    const dx = next[0] - prev[0];
    const dy = next[1] - prev[1];
    const L = Math.hypot(dx, dy);
    if (L > 1e-9) {
      const perpDist = Math.abs((curr[0] - prev[0]) * dy - (curr[1] - prev[1]) * dx) / L;
      if (perpDist < pTol) continue;
    }
    out.push(curr);
  }
  return out.length >= 3 ? out : poly.slice();
}


// Miter/bevel offset with a tight miter limit (1.5×|delta|) so sharp
// convex corners never emit a long spike. Any residual self-intersection
// from a near-reflex bevel is dissolved by polygon-clipping's boolean
// union, which keeps only the outermost boundary.
function offsetPolygon(poly, delta) {
  if (Math.abs(delta) < 1e-6) return poly.slice();
  const n = poly.length;
  if (n < 3) return poly.slice();
  const sign = signedArea(poly) > 0 ? 1 : -1;
  const miterLimit = 1.5 * Math.abs(delta);

  const normals = new Array(n);
  for (let i = 0; i < n; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % n];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const L = Math.hypot(dx, dy) || 1;
    normals[i] = [(sign * dy) / L, (-sign * dx) / L];
  }

  const raw = new Array(n);
  for (let i = 0; i < n; i++) {
    const na = normals[(i - 1 + n) % n];
    const nb = normals[i];
    const c = na[0] * nb[0] + na[1] * nb[1];
    const denom = 1 + c;
    if (Math.abs(denom) < 1e-6 || Math.abs(delta / denom) > miterLimit) {
      // Cap the miter length: push along the bisector but no farther than
      // `miterLimit` from the corner.
      const bx = na[0] + nb[0];
      const by = na[1] + nb[1];
      const bl = Math.hypot(bx, by);
      if (bl < 1e-6) {
        raw[i] = [poly[i][0] + delta * na[0], poly[i][1] + delta * na[1]];
      } else {
        const k = Math.sign(delta) * miterLimit;
        raw[i] = [poly[i][0] + (k * bx) / bl, poly[i][1] + (k * by) / bl];
      }
    } else {
      const k = delta / denom;
      raw[i] = [poly[i][0] + k * (na[0] + nb[0]), poly[i][1] + k * (na[1] + nb[1])];
    }
  }

  // Dissolve any self-intersecting sub-loops the miter cap left behind.
  // For an OUTWARD offset (delta > 0) we take the union of the raw output
  // with the original ring so any spike that dips back INSIDE the polygon
  // is absorbed by the polygon proper — this catches the classic
  // "z-shape" self-crossing that even a well-behaved union can leave when
  // it happens near a reflex corner. For an INWARD offset (delta < 0) the
  // raw output is intersected with the polygon to guarantee it stays
  // inside.
  const outRing = raw.map((p) => [p[0], p[1]]);
  outRing.push([outRing[0][0], outRing[0][1]]);
  const origRing = poly.map((p) => [p[0], p[1]]);
  origRing.push([origRing[0][0], origRing[0][1]]);
  let cleaned;
  try {
    if (delta > 0) {
      cleaned = polygonClipping.union([outRing], [origRing]);
    } else {
      cleaned = polygonClipping.intersection([outRing], [origRing]);
    }
  } catch (e) {
    return raw;
  }
  if (!cleaned || cleaned.length === 0) return raw;
  // Pick the largest outer ring — that's the true offset boundary.
  let best = null;
  let bestArea = -1;
  for (const mp of cleaned) {
    for (let i = 0; i < mp.length; i++) {
      const r = mp[i];
      const a = Math.abs(signedArea(r));
      if (a > bestArea) {
        bestArea = a;
        best = r;
      }
    }
  }
  if (!best || best.length < 4) return raw;
  return simplifyPolygon(best.slice(0, -1), 0.02, 0);
}

function bbox(points) {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const p of points) {
    if (p[0] < minX) minX = p[0];
    if (p[0] > maxX) maxX = p[0];
    if (p[1] < minY) minY = p[1];
    if (p[1] > maxY) maxY = p[1];
  }
  return { minX, maxX, minY, maxY, w: maxX - minX, h: maxY - minY };
}

// Snap a 2D point onto the closest segment of a closed polyline. Used so
// stitch holes always sit exactly on the (possibly smoothed) stitch line —
// otherwise smoothing would leave the holes on the old straight edge while
// the seam curves away.
function snapToPolyline(pt, poly) {
  let best = poly[0];
  let bestD = Infinity;
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % n];
    const ax = b[0] - a[0];
    const ay = b[1] - a[1];
    const l2 = ax * ax + ay * ay;
    let t = 0;
    if (l2 > 1e-12) {
      t = ((pt[0] - a[0]) * ax + (pt[1] - a[1]) * ay) / l2;
      if (t < 0) t = 0;
      else if (t > 1) t = 1;
    }
    const qx = a[0] + t * ax;
    const qy = a[1] + t * ay;
    const dx = qx - pt[0];
    const dy = qy - pt[1];
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      best = [qx, qy];
    }
  }
  return best;
}

// Drop stitch holes that end up too close together after snapping to a
// smoothed perimeter. Common when a sharp concave corner folds the perimeter
// back on itself: adjacent edges' holes project to nearly the same point.
function dedupeHoles(holes, minSpacing) {
  const kept = [];
  const min2 = minSpacing * minSpacing;
  for (const h of holes) {
    let dup = false;
    for (const k of kept) {
      const dx = k.x - h.x;
      const dy = k.y - h.y;
      if (dx * dx + dy * dy < min2) {
        dup = true;
        break;
      }
    }
    if (!dup) kept.push(h);
  }
  return kept;
}

// Global 3D hole dedupe. For every vertex V, look at all holes on edges
// incident to V that are within 2× minSpacing of V, sort them by 3D
// distance to V, and drop any hole that lies within `minSpacing` of an
// already-kept hole. The returned Set of `${edgeKey}#${holeIdx}` strings
// is used to filter holes IDENTICALLY on both panels sharing an edge, so
// stitches line up across every seam.
function globalHoleDropSet(edgeHoles3D, vertices3D, minSpacing) {
  const near = 2 * minSpacing;
  const byVertex = new Map();
  for (const [key, holes] of edgeHoles3D) {
    const [a, b] = key.split('_').map(Number);
    const pA = vertices3D[a];
    const pB = vertices3D[b];
    for (let i = 0; i < holes.length; i++) {
      const p = holes[i].point3D;
      const dA = Math.hypot(p[0] - pA[0], p[1] - pA[1], p[2] - pA[2]);
      const dB = Math.hypot(p[0] - pB[0], p[1] - pB[1], p[2] - pB[2]);
      if (dA < near) {
        if (!byVertex.has(a)) byVertex.set(a, []);
        byVertex.get(a).push({ key, idx: i, point3D: p, d: dA });
      }
      if (dB < near) {
        if (!byVertex.has(b)) byVertex.set(b, []);
        byVertex.get(b).push({ key, idx: i, point3D: p, d: dB });
      }
    }
  }
  const drop = new Set();
  for (const entries of byVertex.values()) {
    entries.sort((x, y) => x.d - y.d);
    const kept = [];
    for (const e of entries) {
      if (drop.has(`${e.key}#${e.idx}`)) continue;
      let tooClose = false;
      for (const k of kept) {
        const dx = e.point3D[0] - k.point3D[0];
        const dy = e.point3D[1] - k.point3D[1];
        const dz = e.point3D[2] - k.point3D[2];
        if (Math.hypot(dx, dy, dz) < minSpacing) {
          tooClose = true;
          break;
        }
      }
      if (tooClose) drop.add(`${e.key}#${e.idx}`);
      else kept.push(e);
    }
  }
  return drop;
}

function shift(points, dx, dy) {
  return points.map(([x, y]) => [x + dx, y + dy]);
}

// Shelf packing: sort panels by height desc, pack row-by-row on a fixed-width
// sheet, growing the sheet height as needed. Bbox includes edge-label chip
// extents so labels outside the cut line still get clear space between panels.
function layoutPanels(panels, sheetWidthMM, marginMM, gapMM) {
  const chipHalfW = 3;   // conservative half-width of a label chip in mm
  const chipHalfH = 1.4; // half-height of the chip
  const withBB = panels.map((p) => {
    const pts = p.expanded.slice();
    for (const el of p.edgeLabels || []) {
      pts.push([el.x - chipHalfW, el.y - chipHalfH]);
      pts.push([el.x + chipHalfW, el.y + chipHalfH]);
    }
    return { ...p, bb: bbox(pts) };
  });
  const sorted = [...withBB].sort((a, b) => b.bb.h - a.bb.h);

  let x = marginMM;
  let y = marginMM;
  let rowH = 0;
  const laid = [];

  for (const p of sorted) {
    const w = p.bb.w;
    const h = p.bb.h;
    if (x + w > sheetWidthMM - marginMM && x > marginMM) {
      x = marginMM;
      y += rowH + gapMM;
      rowH = 0;
    }
    const dx = x - p.bb.minX;
    const dy = y - p.bb.minY;
    laid.push({
      ...p,
      original: shift(p.original, dx, dy),
      expanded: shift(p.expanded, dx, dy),
      foam: p.foam ? shift(p.foam, dx, dy) : null,
      holes: p.holes.map((h) => ({ ...h, x: h.x + dx, y: h.y + dy })),
      apertures: (p.apertures || []).map((a) => ({
        stitch: shift(a.stitch, dx, dy),
        cut: shift(a.cut, dx, dy),
        foam: a.foam ? shift(a.foam, dx, dy) : null,
      })),
      edgeLabels: (p.edgeLabels || []).map((el) => ({
        ...el,
        x: el.x + dx,
        y: el.y + dy,
      })),
      labelPos: [x + w / 2, y + h / 2],
    });
    x += w + gapMM;
    if (h > rowH) rowH = h;
  }
  const totalHeight = y + rowH + marginMM;
  return { panels: laid, sheetHeight: totalHeight };
}

function svgAttr(n) {
  return typeof n === 'number' ? n.toFixed(3) : String(n);
}

// Half-length of each slit when the user picks the awl ("slit") hole style.
// Falls back to 0.5 mm (= 1 mm total) if the caller passes a non-positive
// value, and returns 0 for the normal round-hole style so the SVG writer
// can cheaply skip the slit code path.
function slitHalfLen(style, lengthMM) {
  if (style !== 'slit') return 0;
  const l = Number(lengthMM);
  return (Number.isFinite(l) && l > 0 ? l : 1) / 2;
}


function renderSVG(laid, opts) {
  const {
    sheetWidthMM,
    marginMM,
    holeDiameterMM,
    ballDiameterMM,
    seamAllowanceMM,
    holeSpacingMM,
    totalPanels,
    spherized,
    foamInsetMM = 0,
    holeStyle = 'round',
    slitLengthMM = 1,
  } = opts;
  const H = laid.sheetHeight;
  const r = holeDiameterMM / 2;
  const slitHalf = slitHalfLen(holeStyle, slitLengthMM);
  // Non-cut annotations use a blue stroke so laser software can ignore them
  // (only CUT-black and FOAM-yellow are actual cut lines). Everything is
  // stroke-only (no fills) so nothing gets rasterised on a plotter.
  const ANNOT = '#2563EB';

  const parts = [];
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${sheetWidthMM}mm" height="${svgAttr(H)}mm" viewBox="0 0 ${sheetWidthMM} ${svgAttr(H)}">`,
  );
  parts.push(
    `<style>
      .cut{fill:none;stroke:#000;stroke-width:0.3}
      .foam{fill:none;stroke:#EAB308;stroke-width:0.25}
      .stitch{fill:none;stroke:#DC2626;stroke-width:0.2;stroke-dasharray:1.2,1.2}
      .hole{fill:none;stroke:#16A34A;stroke-width:0.2}
      .edgelbl{font:1.4px 'Helvetica','Arial',sans-serif;fill:${ANNOT};text-anchor:middle;dominant-baseline:middle}
      .label{font:3.6px 'Helvetica','Arial',sans-serif;fill:${ANNOT};text-anchor:middle;font-weight:600}
      .sub{font:2.2px 'Helvetica','Arial',sans-serif;fill:${ANNOT};text-anchor:middle}
      .title{font:4px 'Helvetica','Arial',sans-serif;fill:${ANNOT}}
      .legend{font:3px 'Helvetica','Arial',sans-serif;fill:${ANNOT}}
    </style>`
      .replace(/\s+/g, ' ')
      .trim(),
  );

  // Header + legend
  parts.push(
    `<text class="title" x="${marginMM}" y="${(marginMM - 4).toFixed(2)}">Panels Studio — ${totalPanels} panels · Ø${ballDiameterMM}mm ball${spherized ? ' · spherized' : ''}</text>`,
  );
  parts.push(
    `<text class="legend" x="${marginMM}" y="${(marginMM - 1).toFixed(2)}">CUT: black · STITCH: red dashed · FOAM: yellow (${foamInsetMM > 0 ? `${foamInsetMM}mm inset` : 'disabled'}) · HOLES: green (${holeStyle === 'slit' ? `${slitLengthMM}mm slits @ 45°` : `Ø${holeDiameterMM}mm`} @ ${holeSpacingMM}mm) · BLUE = labels (reference only)</text>`,
  );

  for (const p of laid.panels) {
    parts.push('<g>');
    parts.push(
      `<polygon class="cut" points="${p.expanded.map(([x, y]) => `${x.toFixed(3)},${y.toFixed(3)}`).join(' ')}"/>`,
    );
    // Red dashed stitch line running through the centre of every hole —
    // exactly the panel's original (non-offset) perimeter.
    parts.push(
      `<polygon class="stitch" points="${p.original.map(([x, y]) => `${x.toFixed(3)},${y.toFixed(3)}`).join(' ')}"/>`,
    );
    if (p.foam) {
      parts.push(
        `<polygon class="foam" points="${p.foam.map(([x, y]) => `${x.toFixed(3)},${y.toFixed(3)}`).join(' ')}"/>`,
      );
    }
    // Punched-through inner apertures: each hole gets its own CUT ring
    // (black), STITCH ring (red dashed) and optional FOAM ring (yellow).
    for (const a of p.apertures || []) {
      parts.push(
        `<polygon class="cut" points="${a.cut.map(([x, y]) => `${x.toFixed(3)},${y.toFixed(3)}`).join(' ')}"/>`,
      );
      parts.push(
        `<polygon class="stitch" points="${a.stitch.map(([x, y]) => `${x.toFixed(3)},${y.toFixed(3)}`).join(' ')}"/>`,
      );
      if (a.foam) {
        parts.push(
          `<polygon class="foam" points="${a.foam.map(([x, y]) => `${x.toFixed(3)},${y.toFixed(3)}`).join(' ')}"/>`,
        );
      }
    }
    for (const h of p.holes) {
      if (holeStyle === 'slit') {
        // 45° to the local seam tangent — mimics a hand-held leather awl's
        // diagonal pricking so the finished stitches sit in a saddler's
        // herringbone, not perpendicular to the seam.
        const theta = (h.angle || 0) + Math.PI / 4;
        const dx = Math.cos(theta) * slitHalf;
        const dy = Math.sin(theta) * slitHalf;
        parts.push(
          `<line class="hole" x1="${(h.x - dx).toFixed(3)}" y1="${(h.y - dy).toFixed(3)}" x2="${(h.x + dx).toFixed(3)}" y2="${(h.y + dy).toFixed(3)}"/>`,
        );
      } else {
        parts.push(`<circle class="hole" cx="${h.x.toFixed(3)}" cy="${h.y.toFixed(3)}" r="${r.toFixed(3)}"/>`);
      }
    }
    // Edge labels — each edge shows the neighbour panel number as plain text
    // (no fill pill — laser cutters would otherwise cut the pill outlines too).
    for (const el of p.edgeLabels || []) {
      parts.push(
        `<text class="edgelbl" x="${el.x.toFixed(3)}" y="${el.y.toFixed(3)}">P${el.neighbor + 1}</text>`,
      );
    }
    // Panel identity — number + polygon type + copy count when this panel
    // stands in for multiple congruent siblings (`Cut ×N`).
    const countTxt = p.copyCount > 1 ? ` · Cut ×${p.copyCount}` : '';
    parts.push(
      `<text class="label" x="${p.labelPos[0].toFixed(2)}" y="${p.labelPos[1].toFixed(2)}">P${p.idx + 1}${countTxt}</text>`,
    );
    parts.push(
      `<text class="sub" x="${p.labelPos[0].toFixed(2)}" y="${(p.labelPos[1] + 3.6).toFixed(2)}">${p.type}</text>`,
    );
    parts.push('</g>');
  }
  parts.push('</svg>');
  return parts.join('\n');
}

function panelTypeName(n) {
  if (n === 3) return 'triangle';
  if (n === 4) return 'square';
  if (n === 5) return 'pentagon';
  if (n === 6) return 'hexagon';
  return `${n}-gon`;
}

// Compute a canonical signature for a 2D polygon: cyclic sequence of
// (edge length, signed turn angle). Two panels are congruent iff their
// signatures match under some cyclic rotation and possibly reflection —
// this catches every isometry of a polygon in the plane.
function panelSignature(poly) {
  const n = poly.length;
  const feats = new Array(n);
  for (let i = 0; i < n; i++) {
    const a = poly[(i - 1 + n) % n];
    const b = poly[i];
    const c = poly[(i + 1) % n];
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const v1x = b[0] - a[0], v1y = b[1] - a[1];
    const v2x = c[0] - b[0], v2y = c[1] - b[1];
    const cross = v1x * v2y - v1y * v2x;
    const dot = v1x * v2x + v1y * v2y;
    feats[i] = [len, Math.atan2(cross, dot)];
  }
  return feats;
}

function areCongruent(a, b, lenTol = 0.15, angTol = 0.05) {
  if (a.length !== b.length) return false;
  const n = a.length;
  const match = (off, sign) => {
    for (let i = 0; i < n; i++) {
      const [la, aa] = a[i];
      const j = ((sign > 0 ? off + i : off - i) % n + n) % n;
      const [lb, ab] = b[j];
      if (Math.abs(la - lb) > lenTol) return false;
      if (Math.abs(aa - sign * ab) > angTol) return false;
    }
    return true;
  };
  for (let off = 0; off < n; off++) {
    if (match(off, 1)) return true;
    if (match(off, -1)) return true;
  }
  return false;
}

// Group congruent panels; return only one representative per group with a
// `copyCount` field so the sheet shows every unique panel exactly once.
function dedupePanels(panels) {
  const groups = [];
  const sigs = panels.map((p) => panelSignature(p.original));
  const assigned = new Array(panels.length).fill(false);
  for (let i = 0; i < panels.length; i++) {
    if (assigned[i]) continue;
    // Panels with punched-through holes are treated as one-off — two panels
    // can share an outer-boundary signature yet have completely different
    // inner apertures, so we don't collapse them.
    const iHasHoles = (panels[i].apertures || []).length > 0;
    const members = [i];
    assigned[i] = true;
    if (!iHasHoles) {
      for (let j = i + 1; j < panels.length; j++) {
        if (assigned[j]) continue;
        if ((panels[j].apertures || []).length > 0) continue;
        if (areCongruent(sigs[i], sigs[j])) {
          assigned[j] = true;
          members.push(j);
        }
      }
    }
    groups.push({ rep: panels[i], count: members.length });
  }
  // Group congruent panels; return only one representative per group with a
  // `copyCount` field so the sheet shows every unique panel exactly once.
  return groups.map(({ rep, count }) => ({ ...rep, copyCount: count }));
}

// Place `count` holes evenly along the arc-length of a polyline
// subsegment. `poly` is a closed 2D polyline; `from` and `to` are indices
// into it; the segment walks forward (with wrap-around) from `from` to `to`.
// Holes are placed at fractional arc-length positions (k + 0.5) / count so
// the FIRST hole sits 0.5 spacings inside from the raw vertex — that way,
// the last hole of edge A and the first hole of edge B (either side of a
// shared vertex) are exactly one spacing apart, no gap-in-the-middle.
function distributeHolesOnArc(poly, from, to, count) {
  if (count <= 0) return [];
  const n = poly.length;
  const pts = [];
  let idx = from;
  pts.push(poly[idx]);
  while (idx !== to) {
    idx = (idx + 1) % n;
    pts.push(poly[idx]);
  }
  const lens = [];
  let total = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const dx = pts[i + 1][0] - pts[i][0];
    const dy = pts[i + 1][1] - pts[i][1];
    const l = Math.hypot(dx, dy);
    lens.push(l);
    total += l;
  }
  if (total < 1e-9) return [];
  const holes = [];
  for (let k = 0; k < count; k++) {
    let target = (total * (k + 0.5)) / count;
    for (let i = 0; i < lens.length; i++) {
      if (target <= lens[i] || i === lens.length - 1) {
        const t = lens[i] > 1e-9 ? target / lens[i] : 0;
        const x = pts[i][0] + t * (pts[i + 1][0] - pts[i][0]);
        const y = pts[i][1] + t * (pts[i + 1][1] - pts[i][1]);
        // Local seam tangent — used by the SVG writer when the user picks
        // the awl-slit hole style so each slit sits at 45° to the stitch
        // line, matching a hand-pricked saddler's seam.
        const sdx = pts[i + 1][0] - pts[i][0];
        const sdy = pts[i + 1][1] - pts[i][1];
        const angle = Math.atan2(sdy, sdx);
        holes.push({ x, y, angle });
        break;
      }
      target -= lens[i];
    }
  }
  return holes;
}

// Nearest polyline vertex to a given 2D point.
function nearestPolyIdx(poly, pt) {
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < poly.length; i++) {
    const dx = poly[i][0] - pt[0];
    const dy = poly[i][1] - pt[1];
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

export function buildPanelSheet(vertices, faces, opts = {}) {
  const {
    ballDiameterMM = 213.2,
    seamAllowanceMM = 2.5,
    holeDiameterMM = 1,
    holeSpacingMM = 4,
    sheetWidthMM = 300,
    marginMM = 15,
    gapMM = 8,
    spherized = false,
    edgeSamples = 12,
    foamInsetMM = 0,        // >0 draws yellow foam-liner cut line inset from the stitch line
    labelOffsetMM = 2,      // distance beyond the cut line to place edge labels
    smoothVertices = [],    // vertex indices flagged as smooth corners
    faceHoles = [],         // faceHoles[i] = array of hole rings (each = [vertexIndex, ...]) inside face i
    holeStyle = 'round',    // 'round' = circular drill holes; 'slit' = angled awl pricks
    slitLengthMM = 1,       // length of each slit (only used when holeStyle === 'slit')
  } = opts;
  const smoothSet = new Set(smoothVertices);

  const R = ballDiameterMM / 2;
  const vertices3D = vertices.map((v) => [v[0] * R, v[1] * R, v[2] * R]);

  const edgeHoles3D = computeEdgeHoles(vertices3D, faces, holeSpacingMM, spherized, vertices);
  const keyOf = (a, b) => (a < b ? `${a}_${b}` : `${b}_${a}`);

  // Global (3D) hole dedupe: whenever holes on two edges sharing a vertex
  // would land within `holeSpacingMM * 0.6` of each other in 3D, mark the
  // one closer to the vertex as dropped. Because this decision is made on
  // the shared 3D geometry (not per-panel), BOTH panels adjacent to a seam
  // drop the same holes — the stitch counts stay aligned so panels line up
  // when sewn together.
  const droppedHoles = globalHoleDropSet(edgeHoles3D, vertices3D, holeSpacingMM * 0.6);
  const isDropped = (edgeKey, holeIdx) => droppedHoles.has(`${edgeKey}#${holeIdx}`);

  // Face-adjacency: for each edge, which two faces meet there? Used to label
  // every edge with the neighbouring panel's number so users can match panels
  // without individually numbered holes.
  const edgeFaces = new Map();
  faces.forEach((f, fi) => {
    for (let i = 0; i < f.length; i++) {
      const k = keyOf(f[i], f[(i + 1) % f.length]);
      if (!edgeFaces.has(k)) edgeFaces.set(k, []);
      edgeFaces.get(k).push(fi);
    }
  });
  const neighborOf = (thisFace, a, b) => {
    const pair = edgeFaces.get(keyOf(a, b)) || [];
    return pair.find((f) => f !== thisFace);
  };

  // Shared per-edge hole COUNT — derived from the raw 3D edge length so
  // both panels sharing an edge agree on stitch count regardless of any
  // per-panel smoothing that bends their 2D perimeters differently.
  // Punch-through aperture edges are indexed here too so a parent panel's
  // hole edge and the insert panel's outer edge — which share the same
  // vertex indices — emit identical hole counts and align perfectly.
  const edgeHoleCount = new Map();
  const addEdgeCount = (a, b) => {
    const k = keyOf(a, b);
    if (edgeHoleCount.has(k)) return;
    const pa = vertices3D[a];
    const pb = vertices3D[b];
    const len3D = Math.hypot(pb[0] - pa[0], pb[1] - pa[1], pb[2] - pa[2]);
    edgeHoleCount.set(k, Math.max(1, Math.round(len3D / holeSpacingMM)));
  };
  faces.forEach((face) => {
    for (let i = 0; i < face.length; i++) {
      addEdgeCount(face[i], face[(i + 1) % face.length]);
    }
  });
  faceHoles.forEach((rings) => {
    for (const ring of rings || []) {
      for (let i = 0; i < ring.length; i++) {
        addEdgeCount(ring[i], ring[(i + 1) % ring.length]);
      }
    }
  });

  const panels = faces.map((face, idx) => {
    let originalFlat;
    let holesFlat;
    let cx;
    let cy;
    let edgeLabels;

    // Densify the face perimeter for any vertex marked "smooth" so cut and
    // stitch lines both follow the rounded corner. Sharp vertices are
    // untouched so this is a no-op for classic hard-edged designs.
    const perim3D = smoothPerimeter(face, vertices, smoothSet, 8);
    // Punch-through holes for this face — vertex-index rings that live inside
    // the outer boundary. Each ring is projected into the same 2D frame as
    // the outer perimeter so the export cuts a matching aperture. We keep the
    // ORIGINAL vertex-index rings alongside the 3D-point rings so we can
    // reuse the shared `edgeHoleCount` map (same key = same stitch count on
    // the mating insert panel).
    const holeRingsIdx = faceHoles[idx] || [];
    const holeRings3D = holeRingsIdx.map((ring) =>
      ring.map((vi) => vertices3D[vi]),
    );
    let holePolys2D = [];
    // Parallel to holePolys2D: the 2D positions of each raw ring vertex,
    // used to split the densified ring into per-edge arcs for stitch-hole
    // distribution.
    let holeRingsRaw2D = [];

    if (spherized) {
      const c = [0, 0, 0];
      for (const p of perim3D) {
        c[0] += p[0];
        c[1] += p[1];
        c[2] += p[2];
      }
      const cHat = n3(c);
      const project = makeAzimuthalProjector(cHat, R);

      originalFlat = [];
      for (let i = 0; i < perim3D.length; i++) {
        const a = n3(perim3D[i]);
        const b = n3(perim3D[(i + 1) % perim3D.length]);
        for (let s = 0; s < edgeSamples; s++) {
          const t = s / edgeSamples;
          const uni = slerpUnit(a, b, t);
          originalFlat.push(project(uni));
        }
      }
      // Project hole rings using the same azimuthal projector so their
      // shape on paper matches the panel's flat pattern.
      holePolys2D = holeRings3D.map((ring) => {
        const flat = [];
        for (let i = 0; i < ring.length; i++) {
          const a = n3(ring[i]);
          const b = n3(ring[(i + 1) % ring.length]);
          for (let s = 0; s < edgeSamples; s++) {
            const t = s / edgeSamples;
            flat.push(project(slerpUnit(a, b, t)));
          }
        }
        return flat;
      });
      holeRingsRaw2D = holeRingsIdx.map((ring) =>
        ring.map((vi) => project(n3(vertices[vi]))),
      );
      // One label per original neighbour-adjacent edge, placed at that edge's
      // midpoint on the unit sphere (which lies on the smoothed perimeter).
      edgeLabels = [];
      for (let i = 0; i < face.length; i++) {
        const a = vertices[face[i]];
        const b = vertices[face[(i + 1) % face.length]];
        const mid3 = n3([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2]);
        const other = neighborOf(idx, face[i], face[(i + 1) % face.length]);
        if (other !== undefined) {
          edgeLabels.push({ pos: project(mid3), neighbor: other });
        }
      }
      cx = 0;
      cy = 0;

      // Post-curve hole placement: distribute holes evenly along the ARC
      // LENGTH of each raw edge's actual smoothed 2D subsegment. Split
      // originalFlat at the closest points to the raw vertices' 2D
      // projections. Hole COUNT per edge is the globally shared value from
      // edgeHoleCount so both neighbouring panels emit the same number of
      // holes on their side of every seam.
      const rawV2D = face.map((vi) => project(n3(vertices[vi])));
      const splitIdx = rawV2D.map((p) => nearestPolyIdx(originalFlat, p));
      holesFlat = [];
      for (let i = 0; i < face.length; i++) {
        const a = face[i];
        const b = face[(i + 1) % face.length];
        const n = edgeHoleCount.get(keyOf(a, b)) || 0;
        const arc = distributeHolesOnArc(
          originalFlat,
          splitIdx[i],
          splitIdx[(i + 1) % face.length],
          n,
        );
        holesFlat.push(...arc);
      }
    } else {
      const face3D = face.map((i) => vertices3D[i]);
      const frame = faceFrame(face3D);
      // Smoothing was computed on unit-sphere vertices; scale to the ball
      // radius (vertices3D units) before projecting.
      const perim3DScaled = perim3D.map((p) => [p[0] * R, p[1] * R, p[2] * R]);
      originalFlat = perim3DScaled.map((p) => project(p, frame));
      // Project each hole ring through the same flat frame.
      holePolys2D = holeRings3D.map((ring) =>
        ring.map((p) => project(p, frame)),
      );
      holeRingsRaw2D = holeRingsIdx.map((ring) =>
        ring.map((vi) => project(vertices3D[vi], frame)),
      );

      cx = originalFlat.reduce((s, p) => s + p[0], 0) / originalFlat.length;
      cy = originalFlat.reduce((s, p) => s + p[1], 0) / originalFlat.length;

      edgeLabels = [];
      for (let i = 0; i < face.length; i++) {
        const a = vertices3D[face[i]];
        const b = vertices3D[face[(i + 1) % face.length]];
        const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
        const [mx, my] = project(mid, frame);
        const other = neighborOf(idx, face[i], face[(i + 1) % face.length]);
        if (other !== undefined) {
          edgeLabels.push({ pos: [mx, my], neighbor: other });
        }
      }

      const rawV2D = face.map((vi) => project(vertices3D[vi], frame));
      const splitIdx = rawV2D.map((p) => nearestPolyIdx(originalFlat, p));
      holesFlat = [];
      for (let i = 0; i < face.length; i++) {
        const a = face[i];
        const b = face[(i + 1) % face.length];
        const n = edgeHoleCount.get(keyOf(a, b)) || 0;
        const arc = distributeHolesOnArc(
          originalFlat,
          splitIdx[i],
          splitIdx[(i + 1) % face.length],
          n,
        );
        holesFlat.push(...arc);
      }
    }

    // Push each edge label outward past the cut line so the panel interior
    // and the seam-allowance flap stay clean. Distance from stitch line =
    // seam allowance + labelOffsetMM (labels sit ~labelOffsetMM outside cut).
    const outOffset = seamAllowanceMM + labelOffsetMM;
    const labels = edgeLabels.map(({ pos, neighbor }) => {
      const dx = pos[0] - cx;
      const dy = pos[1] - cy;
      const dl = Math.hypot(dx, dy) || 1;
      return {
        x: pos[0] + (dx / dl) * outOffset,
        y: pos[1] + (dy / dl) * outOffset,
        neighbor,
      };
    });

    const expanded = offsetPolygon(originalFlat, seamAllowanceMM);
    const foam =
      foamInsetMM > 0 ? offsetPolygon(originalFlat, -foamInsetMM) : null;
    // Build per-hole cut / stitch / foam rings. The panel material lies
    // OUTSIDE a hole ring, so the seam flap (CUT) extends INTO the hole
    // (inward offset, shrinking the aperture) and the foam inset goes
    // outward into the material — mirroring the outer perimeter.
    const holes2D = holePolys2D.map((ring) => ({
      stitch: ring,
      cut: offsetPolygon(ring, -seamAllowanceMM),
      foam: foamInsetMM > 0 ? offsetPolygon(ring, foamInsetMM) : null,
    }));
    // Stitch holes around each aperture's interior stitch line. Count PER
    // RAW EDGE is derived from the same shared `edgeHoleCount` map as the
    // outer perimeter, so a parent panel's aperture edge and the mating
    // insert panel's outer edge — which share vertex indices — emit exactly
    // matching hole counts and positions.
    for (let ri = 0; ri < holePolys2D.length; ri++) {
      const ring2D = holePolys2D[ri];
      const rawIdxRing = holeRingsIdx[ri];
      const rawV2D = holeRingsRaw2D[ri];
      if (!ring2D || ring2D.length < 3 || !rawIdxRing) continue;
      const splitIdx = rawV2D.map((p) => nearestPolyIdx(ring2D, p));
      for (let i = 0; i < rawIdxRing.length; i++) {
        const a = rawIdxRing[i];
        const b = rawIdxRing[(i + 1) % rawIdxRing.length];
        const n = edgeHoleCount.get(keyOf(a, b)) || 0;
        const arc = distributeHolesOnArc(
          ring2D,
          splitIdx[i],
          splitIdx[(i + 1) % rawIdxRing.length],
          n,
        );
        holesFlat.push(...arc);
      }
    }
    return {
      idx,
      original: originalFlat,
      expanded,
      foam,
      holes: holesFlat,
      apertures: holes2D,
      edgeLabels: labels,
      type: panelTypeName(face.length),
    };
  });

  const uniquePanels = dedupePanels(panels);
  const laid = layoutPanels(uniquePanels, sheetWidthMM, marginMM, gapMM);
  const commonOpts = {
    sheetWidthMM,
    marginMM,
    holeDiameterMM,
    ballDiameterMM,
    seamAllowanceMM,
    holeSpacingMM,
    totalPanels: panels.length,
    spherized,
    foamInsetMM,
    holeStyle,
    slitLengthMM,
  };
  const svg = renderSVG(laid, commonOpts);

  return {
    svg,
    sheetHeightMM: laid.sheetHeight,
    panels: panels.length,
    uniquePanels: uniquePanels.length,
    // Exposed so alternative renderers (DXF, PDF, etc.) can reuse the same
    // laid-out geometry without re-running the whole projection pipeline.
    laid,
    renderOpts: commonOpts,
  };
}
